<template>
  <div class="vip-page">
    <!-- Header -->
    <div class="vip-header">
      <van-icon name="arrow-left" size="22" color="#fff" @click="$router.go(-1)" />
      <h1 class="vip-header__title">{{ $lang.vip_title }}</h1>
      <div class="vip-header__balance">
        <span>{{ getCurrency }} {{ $formatNumberWithCommas(balance) }}</span>
        <div class="vip-header__add" @click="$jumpTo('/rechargeCont')">
          <van-icon name="plus" size="12" color="#000" />
        </div>
      </div>
    </div>
    <div class="vip-diagnostics">
      <div v-if="balanceReadError" class="vip-diagnostic">{{ balanceReadError }}</div>
      <div v-if="vipReadError" class="vip-diagnostic">{{ vipReadError }}</div>
      <div v-if="giftReadError" class="vip-diagnostic">{{ giftReadError }}</div>
      <div v-if="rewardActionBlockedReason" class="vip-diagnostic vip-diagnostic--blocked">{{ rewardActionBlockedReason }}</div>
    </div>

    <!-- Level Emblem -->
    <div class="vip-emblem">
      <div class="vip-emblem__arrow" @click="prevLevel">
        <van-icon name="arrow-left" size="20" color="#fff" />
      </div>
      <div class="vip-emblem__badge">
        <img
          v-if="currentViewLevel <= currentLevel"
          :src="levelBadge"
          class="vip-emblem__img"
        />
        <div v-else class="vip-emblem__locked">
          <img :src="levelBadge" class="vip-emblem__img vip-emblem__img--locked" />
          <div class="vip-emblem__lock-overlay">
            <van-icon name="lock" size="36" color="#fff" />
          </div>
        </div>
        <p class="vip-emblem__name">{{ $lang.vip_level_text || 'Level' }} {{ currentViewLevel }} <span class="vip-emblem__tier" :class="{ 'vip-emblem__tier--inferno': currentTierName === 'Inferno' }">{{ currentTierName }}</span></p>
      </div>
      <div class="vip-emblem__arrow" @click="nextLevel">
        <van-icon name="arrow" size="20" color="#fff" />
      </div>
    </div>

    <!-- Progress -->
    <div class="vip-progress">
      <!-- Recharge Progress -->
      <div class="vip-progress__header">
        <span class="vip-progress__label">{{ $lang.vip_recharge_progress || 'RECHARGE PROGRESS' }}</span>
        <div class="vip-progress__xp">
          <span class="vip-progress__current">{{ $formatNumberWithCommas(rechargeAmount) }}</span>
          <span class="vip-progress__sep"> / </span>
          <span class="vip-progress__max">{{ $formatNumberWithCommas(nextRechargeAmount) }}</span>
        </div>
      </div>
      <div class="vip-progress__bar">
        <div class="vip-progress__fill" :style="{ width: rechargePercent + '%' }"></div>
      </div>

      <p class="vip-progress__remaining">
        {{ $lang.vip_next_level || 'NEXT LEVEL' }}: VIP {{ currentLevel + 1 }}
      </p>
    </div>

    <!-- Privileges -->
    <div v-if="currentViewLevel > 0" class="vip-privileges">
      <div class="vip-privileges__title">
        <span class="vip-privileges__bar"></span>
        {{ $lang.vip_exclusive_privileges }}
      </div>
      <div class="vip-privileges__grid">
        <div
          v-for="(item, idx) in privileges"
          :key="idx"
          class="vip-card"
          :class="{ 'vip-card--full': item.full, 'vip-card--locked': isLocked }"
        >
          <div v-if="isLocked" class="vip-card__lock">
            <van-icon name="lock" size="12" color="rgba(255,255,255,0.4)" />
          </div>
          <div class="vip-card__content" :class="{ 'vip-card__content--row': item.full }">
            <div class="vip-card__icon">
              <van-icon :name="item.icon" size="22" color="#ffb020" />
            </div>
            <div>
              <p class="vip-card__label">{{ item.label }}</p>
              <p class="vip-card__value" :class="{ 'vip-card__value--locked': isLocked }">
                {{ item.value }}
              </p>
            </div>
          </div>
          <!-- Claim button for claimable privileges -->
          <button
            v-if="item.claimKey && !isLocked"
            class="vip-card__claim"
            :class="{
              'vip-card__claim--active': getGiftStatus(item.claimKey) === 1,
              'vip-card__claim--done': getGiftStatus(item.claimKey) === 2
            }"
            :disabled="getGiftStatus(item.claimKey) !== 1 || getGiftClaiming(item.claimKey) || rewardActionBlockedReason"
            @click="claimByKey(item.claimKey)"
          >
            {{ getGiftClaiming(item.claimKey) ? '...' : btnText(getGiftStatus(item.claimKey)) }}
          </button>
        </div>
      </div>
    </div>

    <!-- VIP Gifts -->
    <div v-if="currentViewLevel > 0" class="vip-gifts">
      <div class="vip-gifts__title">
        <span class="vip-gifts__bar"></span>
        {{ $lang.vip_my_gifts || 'MY GIFTS' }}
      </div>
      <div class="vip-gifts__list">
        <!-- Upgrade Bonus -->
        <div class="vip-gift-item">
          <div class="vip-gift-item__icon">
            <van-icon name="gift-o" size="22" color="#ffb020" />
          </div>
          <div class="vip-gift-item__body">
            <p class="vip-gift-item__name">{{ $lang.vip_level_up_reward }}</p>
            <p class="vip-gift-item__amount" v-if="(currentViewConfig.upgradeBonus || 0) > 0">{{ getCurrency }} {{ $formatNumberWithCommas(currentViewConfig.upgradeBonus) }}</p>
            <p class="vip-gift-item__status" v-else>{{ statusText(upgradeStatus) }}</p>
          </div>
          <button
            class="vip-gift-item__btn"
            :class="{
              'vip-gift-item__btn--claim': upgradeStatus === 1,
              'vip-gift-item__btn--done': upgradeStatus === 2,
              'vip-gift-item__btn--locked': upgradeStatus === 0
            }"
            :disabled="upgradeStatus !== 1 || claimingUpgrade || rewardActionBlockedReason"
            @click="claimUpgrade(currentViewLevel)"
          >
            {{ claimingUpgrade ? '...' : btnText(upgradeStatus) }}
          </button>
        </div>

        <!-- Daily Reward -->
        <div class="vip-gift-item">
          <div class="vip-gift-item__icon">
            <van-icon name="calendar-o" size="22" color="#ffb020" />
          </div>
          <div class="vip-gift-item__body">
            <p class="vip-gift-item__name">{{ $lang.vip_daily_reward }}</p>
            <p class="vip-gift-item__amount" v-if="signViewReward > 0">{{ getCurrency }} {{ $formatNumberWithCommas(signViewReward) }}</p>
            <p class="vip-gift-item__status" v-else>{{ statusText(signStatus) }}</p>
          </div>
          <button
            class="vip-gift-item__btn"
            :class="{
              'vip-gift-item__btn--claim': signStatus === 1,
              'vip-gift-item__btn--done': signStatus === 2,
              'vip-gift-item__btn--locked': signStatus === 0
            }"
            :disabled="signStatus !== 1 || claimingSign || rewardActionBlockedReason"
            @click="claimSign"
          >
            {{ claimingSign ? '...' : btnText(signStatus) }}
          </button>
        </div>

        <!-- Birthday Gift -->
        <div class="vip-gift-item">
          <div class="vip-gift-item__icon">
            <van-icon name="star-o" size="22" color="#ffb020" />
          </div>
          <div class="vip-gift-item__body">
            <p class="vip-gift-item__name">{{ $lang.vip_birthday_gift }}</p>
            <p class="vip-gift-item__amount" v-if="(currentViewConfig.monthlyBonus || 0) > 0">{{ getCurrency }} {{ $formatNumberWithCommas(currentViewConfig.monthlyBonus) }}</p>
            <p class="vip-gift-item__status" v-else>{{ statusText(birthdayStatus) }}</p>
          </div>
          <button
            class="vip-gift-item__btn"
            :class="{
              'vip-gift-item__btn--claim': birthdayStatus === 1,
              'vip-gift-item__btn--done': birthdayStatus === 2,
              'vip-gift-item__btn--locked': birthdayStatus === 0
            }"
            :disabled="birthdayStatus !== 1 || claimingBirthday || rewardActionBlockedReason"
            @click="claimBirthday"
          >
            {{ claimingBirthday ? '...' : btnText(birthdayStatus) }}
          </button>
        </div>

        <!-- Loss Rebate -->
        <div class="vip-gift-item">
          <div class="vip-gift-item__icon">
            <van-icon name="exchange" size="22" color="#ffb020" />
          </div>
          <div class="vip-gift-item__body">
            <p class="vip-gift-item__name">{{ $lang.vip_loss_rebate }}</p>
            <p class="vip-gift-item__amount" v-if="(currentViewConfig.signBalLimit || 0) > 0">{{ getCurrency }} {{ $formatNumberWithCommas(currentViewConfig.signBalLimit) }}</p>
            <p class="vip-gift-item__status" v-else>{{ statusText(cashBackStatus) }}</p>
          </div>
          <button
            class="vip-gift-item__btn"
            :class="{
              'vip-gift-item__btn--claim': cashBackStatus === 1,
              'vip-gift-item__btn--done': cashBackStatus === 2,
              'vip-gift-item__btn--locked': cashBackStatus === 0
            }"
            :disabled="cashBackStatus !== 1 || claimingCashBack || rewardActionBlockedReason"
            @click="claimCashBack"
          >
            {{ claimingCashBack ? '...' : btnText(cashBackStatus) }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { VipInit, GetVipAwardInit, GetVipAward, GetSignIn, SignIn, BirthdayAwardInit, ReceiveBirthdayAward, CashBackInit, GetCashBack, GameBalanceList } from '@/api/common'
import { contentOf, failMessage, handleNeedLogin, isOk, isReadOnlyQuerySuccess, safeParseJson, toArray, toNumber } from './safeFlow'
import { hasUnavailableMarker, isRewardClaimSuccessWithProof as rewardClaimSuccess } from '@/views/details/activity/rewardApi'
import { vipTierName } from '@/utils/vipTier' // VIP 数字级→品牌命名档(Bronze/Silver/Gold/Inferno)
import { isUnavailableToken as coreIsUnavailableToken } from '@/utils/responseGuard'

function safeToast(vm, payload) {
  if (vm && typeof vm.$toast === 'function') {
    vm.$toast(payload)
  }
}

// VIP_REWARD_UNAVAILABLE_TOKENS 已删除 —— token 分类走 responseGuard 核 union
const VIP_REWARD_SCAN_KEYS = [
  'status',
  'state',
  'result',
  'code',
  'errorCode',
  'readOnly',
  'readOnlyMode',
  'read_only_mode',
  'readOnlyReward',
  'read_only_reward',
  'readOnlyAction',
  'read_only_action',
  'fallback',
  'readonly',
  'read_only',
  'mock',
  'mockMode',
  'mock_mode',
  'mockFallback',
  'mock_fallback',
  'unimplemented',
  'notImplemented',
  'not_implemented',
  'notSupported',
  'not_supported',
  'notConnected',
  'not_connected',
  'unsupported',
  'dataUnavailable',
  'data_unavailable',
  'unavailable',
  'readUnavailable',
  'read_unavailable',
  'readBlocked',
  'read_blocked',
  'readFailure',
  'read_failure',
  'readFailed',
  'read_failed',
  'stub',
  'writeBlocked',
  'write_blocked',
  'backendBlocked',
  'backend_blocked',
  'backendFailure',
  'backend_failure',
  'technicalMarker',
  'technical_marker',
  'failClosed',
  'fail_closed',
  'paymentBackendBlocked',
  'payment_backend_blocked',
  'paymentStatus',
  'payment_status',
  'paymentStatusUnconfirmed',
  'payment_status_unconfirmed',
  'falseProof',
  'false_proof',
  'unconfirmed',
  'rewardBackendBlocked',
  'reward_backend_blocked',
  'rewardProof',
  'reward_proof',
  'writeProof',
  'write_proof',
  'affectedRowsProof',
  'affected_rows_proof',
  'successProof',
  'success_proof',
  'serverProof',
  'server_proof',
  'affectedProof',
  'affected_proof',
  'success',
  'receiveSuccess',
  'claimSuccess',
  'signSuccess',
  'paySuccess',
  'receiveStatus',
  'claimStatus',
  'promoStatus'
]
const VIP_REWARD_FLAG_KEYS = [
  'readOnly',
  'readOnlyMode',
  'read_only_mode',
  'readOnlyReward',
  'read_only_reward',
  'readOnlyAction',
  'read_only_action',
  'fallback',
  'readonly',
  'read_only',
  'mock',
  'mockMode',
  'mock_mode',
  'mockFallback',
  'mock_fallback',
  'unimplemented',
  'notImplemented',
  'not_implemented',
  'notSupported',
  'not_supported',
  'notConnected',
  'not_connected',
  'unsupported',
  'dataUnavailable',
  'data_unavailable',
  'unavailable',
  'readUnavailable',
  'read_unavailable',
  'readBlocked',
  'read_blocked',
  'readFailure',
  'read_failure',
  'readFailed',
  'read_failed',
  'stub',
  'writeBlocked',
  'write_blocked',
  'backendBlocked',
  'backend_blocked',
  'backendFailure',
  'backend_failure',
  'technicalMarker',
  'technical_marker',
  'failClosed',
  'fail_closed',
  'paymentBackendBlocked',
  'payment_backend_blocked',
  'paymentStatusUnconfirmed',
  'payment_status_unconfirmed',
  'falseProof',
  'false_proof',
  'unconfirmed',
  'rewardBackendBlocked',
  'reward_backend_blocked'
]
const VIP_REWARD_SUCCESS_KEYS = [
  'success',
  'receiveSuccess',
  'claimSuccess',
  'signSuccess',
  'paySuccess',
  'rewardProof',
  'reward_proof',
  'writeProof',
  'write_proof',
  'affectedRowsProof',
  'affected_rows_proof',
  'successProof',
  'success_proof',
  'serverProof',
  'server_proof',
  'affectedProof',
  'affected_proof'
]
const VIP_REWARD_MAX_SCAN_DEPTH = 8

function vipRewardToken(value) {
  return String(value == null ? '' : value).trim().toLowerCase()
}

function isVipRewardUnavailableToken(value) {
  return coreIsUnavailableToken(value)
}

function isActiveVipRewardMarker(value) {
  const token = vipRewardToken(value)
  return value === true || value === 1 || ['true', '1', 'yes', 'on'].includes(token) || isVipRewardUnavailableToken(value)
}

function hasVipRewardUnavailableMarker(payload, depth = 0, seen = []) {
  if (!payload || typeof payload !== 'object' || depth > VIP_REWARD_MAX_SCAN_DEPTH || seen.includes(payload)) return false
  seen.push(payload)
  if (Array.isArray(payload)) {
    return payload.some(item => hasVipRewardUnavailableMarker(item, depth + 1, seen))
  }
  if (VIP_REWARD_FLAG_KEYS.some(key => isActiveVipRewardMarker(payload[key]))) {
    return true
  }
  if (['falseProof', 'false_proof'].some(key => payload[key] !== undefined && payload[key] !== null && payload[key] !== '')) {
    return true
  }
  if (VIP_REWARD_SUCCESS_KEYS.some(key => payload[key] === false || payload[key] === 0 || ['false', '0', 'no', 'off', 'placeholder'].includes(vipRewardToken(payload[key])))) {
    return true
  }
  if (VIP_REWARD_SCAN_KEYS.some(key => isVipRewardUnavailableToken(payload[key]))) {
    return true
  }
  return Object.keys(payload).some(key => {
    const value = payload[key]
    if (value && typeof value === 'object') {
      return hasVipRewardUnavailableMarker(value, depth + 1, seen)
    }
    return isVipRewardUnavailableToken(value)
  })
}

function isReadOk(res) {
  return isOk(res) && !hasVipRewardUnavailableMarker(res)
}

function readOnlyBlockMessage(res, fallback) {
  return failMessage(res, fallback)
}

function isSafeReadOnlyQuery(res) {
  return typeof isReadOnlyQuerySuccess === 'function' && isReadOnlyQuerySuccess(res)
}

function isReadUnavailable(res) {
  const payload = res && res.response && res.response.data ? res.response.data : res
  return hasUnavailableMarker(payload) || hasVipRewardUnavailableMarker(payload)
}

function safeReadFailureToast(vm, res, payload) {
  if (!isReadUnavailable(res)) safeToast(vm, payload)
}

export default {
  name: 'VipLevels',
  data() {
    return {
      balance: 0,
      currentLevel: 0,
      currentViewLevel: 0,
      vipList: [],
      betAmount: 0,
      rechargeAmount: 0,
      nextBetAmount: 0,
      nextRechargeAmount: 0,
      // Gift claim states
      upgradeRuleList: [],
      claimingUpgrade: false,
      signStatus: 0,
      signAward: 0,
      signRuleList: [],
      claimingSign: false,
      birthdayStatus: 0,
      birthdayAward: 0,
      claimingBirthday: false,
      cashBackStatus: 0,
      cashBackAmount: 0,
      claimingCashBack: false,
      balanceReadError: '',
      vipReadError: '',
      giftReadError: '',
      rewardActionBlockedReason: ''
    }
  },
  computed: {
    totalLevels() {
      return this.vipList.length > 0 ? this.vipList.length - 1 : 0
    },
    rechargePercent() {
      return this.nextRechargeAmount > 0
        ? Math.min((this.rechargeAmount / this.nextRechargeAmount) * 100, 100)
        : 100
    },
    betPercent() {
      return this.nextBetAmount > 0
        ? Math.min((this.betAmount / this.nextBetAmount) * 100, 100)
        : 100
    },
    upgradeStatus() {
      const rule = this.upgradeRule()
      return rule ? rule.receiveStatus : 0
    },
    upgradeAward() {
      const rule = this.upgradeRule()
      return rule ? rule.reward : 0
    },
    isLocked() {
      return this.currentViewLevel > this.currentLevel
    },
    levelBadge() {
      try {
        return require(`@/assets/img/vip/${this.currentViewLevel}.png`)
      } catch (e) {
        return ''
      }
    },
    currentViewConfig() {
      return this.vipList.find(v => v.vipLevel === this.currentViewLevel) || {}
    },
    currentTierName() {
      return vipTierName(this.currentViewLevel) // 品牌命名档(显示层)
    },
    signViewReward() {
      const effectiveLevel = Math.max(this.currentViewLevel, this.currentLevel)
      if (effectiveLevel === this.currentLevel && this.signAward > 0) {
        return this.signAward
      }
      const rule = this.signRuleList.find(r => r.vipLevel === effectiveLevel)
      return rule ? rule.reward : 0
    },
    privileges() {
      const c = this.getCurrency
      const cfg = this.currentViewConfig
      return [
        { icon: 'gift-o', label: this.$lang.vip_level_up_reward, value: c + ' ' + this.$formatNumberWithCommas(cfg.upgradeBonus || 0), claimKey: 'upgrade' },
        { icon: 'calendar-o', label: this.$lang.vip_daily_reward, value: c + ' ' + this.$formatNumberWithCommas(this.signViewReward || 0), claimKey: 'sign' },
        { icon: 'star-o', label: this.$lang.vip_birthday_gift, value: c + ' ' + this.$formatNumberWithCommas(cfg.monthlyBonus || 0), claimKey: 'birthday' },
        { icon: 'balance-o', label: this.$lang.vip_withdrawal_fee, value: (cfg.withdrawFee != null ? cfg.withdrawFee + '%' : '--') },
        { icon: 'add-o', label: this.$lang.vip_daily_withdrawal, value: (cfg.withdrawCount || 0) + '' },
        { icon: 'cash-back-record', label: this.$lang.vip_withdrawal_limit, value: c + ' ' + this.$formatNumberWithCommas(cfg.withdrawLimit || 0) },
        { icon: 'exchange', label: this.$lang.vip_loss_rebate, value: c + ' ' + this.$formatNumberWithCommas(cfg.signBalLimit || 0), full: true, claimKey: 'cashBack' }
      ]
    }
  },
  mounted() {
    if (this.token) {
      this.loadBalance()
      this.loadVipInit()
      this.loadGiftStatuses()
    } else {
      this.requireLogin()
    }
  },
  methods: {
    requireLogin() {
      this.$jumpTo('/home', {}, { replace: true })
    },
    parseRuleList(value) {
      if (typeof value === 'string') {
        return toArray(safeParseJson(value, []))
      }
      return toArray(value)
    },
    statusText(s) {
      if (s === 0) return this.$lang.vip_not_reached || 'Not reached'
      if (s === 1) return this.$lang.vip_claimable || 'Claimable'
      if (s === 2) return this.$lang.vip_claimed || 'Claimed'
      return '--'
    },
    btnText(s) {
      if (s === 0) return this.$lang.vip_not_reached || 'Locked'
      if (s === 1) return this.$lang.ng_claim || 'Claim'
      if (s === 2) return this.$lang.vip_claimed || 'Claimed'
      return '--'
    },
    getGiftStatus(key) {
      const map = { upgrade: this.upgradeStatus, sign: this.signStatus, birthday: this.birthdayStatus, cashBack: this.cashBackStatus }
      return map[key] || 0
    },
    getGiftClaiming(key) {
      const map = { upgrade: this.claimingUpgrade, sign: this.claimingSign, birthday: this.claimingBirthday, cashBack: this.claimingCashBack }
      return map[key] || false
    },
    isReceiveSuccess(res) {
      return !hasVipRewardUnavailableMarker(res) && rewardClaimSuccess(res)
    },
    resetVipReadState() {
      this.currentLevel = 0
      this.currentViewLevel = 0
      this.vipList = []
      this.betAmount = 0
      this.rechargeAmount = 0
      this.nextBetAmount = 0
      this.nextRechargeAmount = 0
    },
    resetGiftStatusState() {
      this.upgradeRuleList = []
      this.signStatus = 0
      this.signAward = 0
      this.signRuleList = []
      this.birthdayStatus = 0
      this.birthdayAward = 0
      this.cashBackStatus = 0
      this.cashBackAmount = 0
    },
    claimAmount(content, fallback) {
      return toNumber(content.awardAmount || content.amount || content.rewardAmount || content.backAmount || content.reliefAmount || fallback)
    },
    upgradeRule(targetLevel) {
      if (!Array.isArray(this.upgradeRuleList)) return null
      const level = targetLevel != null ? Number(targetLevel) || 0 : this.currentViewLevel
      return this.upgradeRuleList.find(r => Number(r.vipLevel || r.level) === level) || null
    },
    upgradeGiftNo(targetLevel) {
      const rule = this.upgradeRule(targetLevel)
      if (!rule) return ''
      return String(rule.giftNo || rule.recordNo || rule.orderNo || '').trim()
    },
    claimByKey(key) {
      if (this.hasRewardActionBlock()) return
      const map = { upgrade: this.claimUpgrade, sign: this.claimSign, birthday: this.claimBirthday, cashBack: this.claimCashBack }
      if (map[key]) map[key](this.currentViewLevel)
    },
    hasRewardActionBlock() {
      if (!this.rewardActionBlockedReason) return false
      safeToast(this, { message: this.rewardActionBlockedReason, icon: 'cross' })
      return true
    },
    async loadBalance() {
      try {
        const data = await GameBalanceList()
        if (handleNeedLogin(this, data, this.$lang.login_txt11 || this.$lang.Entrar || 'Login Now', { isValue: 'isLogin' }, { replace: true })) return
        if (isReadOk(data) || isSafeReadOnlyQuery(data)) {
          this.balanceReadError = ''
          const c = contentOf(data)
          this.balance = toNumber(c.totalBalance || c.balance || c._balUsable)
        } else {
          this.balanceReadError = failMessage(data, 'This feature is temporarily unavailable. Please try again later.')
          safeReadFailureToast(this, data, { message: this.balanceReadError, icon: 'cross' })
        }
      } catch (e) {
        this.balanceReadError = failMessage(e, 'This feature is temporarily unavailable. Please try again later.')
        safeReadFailureToast(this, e, { message: this.balanceReadError, icon: 'cross' })
      }
    },
    async loadVipInit() {
      try {
        const data = await VipInit()
        if (handleNeedLogin(this, data, this.$lang.login_txt11 || this.$lang.Entrar || 'Login Now', { isValue: 'isLogin' }, { replace: true })) return
        const isReadOnlyQuery = isSafeReadOnlyQuery(data)
        if (isReadOk(data) || isReadOnlyQuery) {
          this.vipReadError = ''
          if (isReadOnlyQuery) {
            this.rewardActionBlockedReason = readOnlyBlockMessage(data, 'This feature is temporarily unavailable. Please try again later.')
          }
          const c = contentOf(data)
          this.currentLevel = toNumber(c.vipLevel)
          this.currentViewLevel = toNumber(c.vipLevel)
          this.vipList = toArray(c.vipList)
          this.betAmount = toNumber(c.betAmount)
          this.rechargeAmount = toNumber(c.rechargeAmount)
          this.nextBetAmount = toNumber(c.nextBetAmount)
          this.nextRechargeAmount = toNumber(c.nextRechargeAmount)
        } else {
          this.vipReadError = failMessage(data, 'This feature is temporarily unavailable. Please try again later.')
          safeReadFailureToast(this, data, { message: this.vipReadError, icon: 'cross' })
        }
      } catch (e) {
        this.vipReadError = failMessage(e, 'This feature is temporarily unavailable. Please try again later.')
        safeReadFailureToast(this, e, { message: this.vipReadError, icon: 'cross' })
      }
    },
    prevLevel() {
      if (this.currentViewLevel > 0) this.currentViewLevel--
    },
    nextLevel() {
      if (this.currentViewLevel < this.totalLevels) this.currentViewLevel++
    },
    async loadGiftStatuses() {
      try {
        const [upRes, signRes, bdRes, cbRes] = await Promise.all([
          GetVipAwardInit({}),
          GetSignIn({}),
          BirthdayAwardInit({}),
          CashBackInit({})
        ])
        if (handleNeedLogin(this, upRes, this.$lang.login_txt11 || this.$lang.Entrar || 'Login Now', { isValue: 'isLogin' }, { replace: true }) ||
          handleNeedLogin(this, signRes, this.$lang.login_txt11 || this.$lang.Entrar || 'Login Now', { isValue: 'isLogin' }, { replace: true }) ||
          handleNeedLogin(this, bdRes, this.$lang.login_txt11 || this.$lang.Entrar || 'Login Now', { isValue: 'isLogin' }, { replace: true }) ||
          handleNeedLogin(this, cbRes, this.$lang.login_txt11 || this.$lang.Entrar || 'Login Now', { isValue: 'isLogin' }, { replace: true })) return
        let giftReadError = ''
        const upgradeReadOnly = isSafeReadOnlyQuery(upRes)
        const signReadOnly = isSafeReadOnlyQuery(signRes)
        const birthdayReadOnly = isSafeReadOnlyQuery(bdRes)
        const cashBackReadOnly = isSafeReadOnlyQuery(cbRes)
        if (isReadOk(upRes)) {
          this.upgradeRuleList = this.parseRuleList(contentOf(upRes).ruleList)
        } else if (upgradeReadOnly) {
          giftReadError = giftReadError || readOnlyBlockMessage(upRes, 'This feature is temporarily unavailable. Please try again later.')
        } else {
          const message = failMessage(upRes, 'This feature is temporarily unavailable. Please try again later.')
          giftReadError = giftReadError || message
          safeReadFailureToast(this, upRes, { message, icon: 'cross' })
        }
        if (isReadOk(signRes)) {
          const signContent = contentOf(signRes)
          this.signStatus = toNumber(signContent.receiveStatus)
          this.signAward = toNumber(signContent.awardAmount)
          this.signRuleList = this.parseRuleList(signContent.ruleList)
        } else if (signReadOnly) {
          giftReadError = giftReadError || readOnlyBlockMessage(signRes, 'This feature is temporarily unavailable. Please try again later.')
        } else {
          const message = failMessage(signRes, 'This feature is temporarily unavailable. Please try again later.')
          giftReadError = giftReadError || message
          safeReadFailureToast(this, signRes, { message, icon: 'cross' })
        }
        if (isReadOk(bdRes)) {
          const birthdayContent = contentOf(bdRes)
          this.birthdayStatus = toNumber(birthdayContent.receiveStatus)
          this.birthdayAward = toNumber(birthdayContent.awardAmount)
        } else if (birthdayReadOnly) {
          giftReadError = giftReadError || readOnlyBlockMessage(bdRes, 'This feature is temporarily unavailable. Please try again later.')
        } else {
          const message = failMessage(bdRes, 'This feature is temporarily unavailable. Please try again later.')
          giftReadError = giftReadError || message
          safeReadFailureToast(this, bdRes, { message, icon: 'cross' })
        }
        if (isReadOk(cbRes)) {
          const cashBackContent = contentOf(cbRes)
          this.cashBackStatus = toNumber(cashBackContent.receiveStatus)
          this.cashBackAmount = toNumber(cashBackContent.backAmount)
        } else if (cashBackReadOnly) {
          giftReadError = giftReadError || readOnlyBlockMessage(cbRes, 'This feature is temporarily unavailable. Please try again later.')
        } else {
          const message = failMessage(cbRes, 'This feature is temporarily unavailable. Please try again later.')
          giftReadError = giftReadError || message
          safeReadFailureToast(this, cbRes, { message, icon: 'cross' })
        }
        this.giftReadError = giftReadError
        this.rewardActionBlockedReason = giftReadError
      } catch (e) {
        this.giftReadError = failMessage(e, 'This feature is temporarily unavailable. Please try again later.')
        this.rewardActionBlockedReason = this.giftReadError
        safeReadFailureToast(this, e, { message: this.giftReadError, icon: 'cross' })
      }
    },
    async claimUpgrade(targetLevel) {
      if (this.claimingUpgrade) return
      if (this.hasRewardActionBlock()) return
      this.claimingUpgrade = true
      try {
        const level = targetLevel != null ? targetLevel : this.currentViewLevel
        const giftNo = this.upgradeGiftNo(level)
        if (!giftNo) {
          safeToast(this, { message: this.$lang.vip_reward_unavailable || 'This feature is temporarily unavailable. Please try again later.', icon: 'cross' })
          return
        }
        const res = await GetVipAward({ giftNo })
        if (handleNeedLogin(this, res)) return
        if (this.isReceiveSuccess(res)) {
          this.rewardActionBlockedReason = ''
          const content = contentOf(res)
          const claimedGiftNo = String(content.giftNo || content.recordNo || giftNo)
          const rule = this.upgradeRuleList.find(r => String(r.giftNo || r.recordNo || r.orderNo || '') === claimedGiftNo)
          if (rule) {
            this.$set(rule, 'receiveStatus', 2)
          }
          const fallbackRule = this.upgradeRule(level)
          const amount = this.claimAmount(content, fallbackRule ? this.ruleValue(fallbackRule, 'upgrade') : this.upgradeAward)
          await this.loadGiftStatuses()
          safeToast(this, { message: '+' + this.getCurrency + this.$formatNumberWithCommas(amount), icon: 'success' })
        } else {
          this.rewardActionBlockedReason = failMessage(res, 'This feature is temporarily unavailable. Please try again later.')
          safeToast(this, { message: this.rewardActionBlockedReason, icon: 'cross' })
        }
      } catch (e) {
        this.rewardActionBlockedReason = failMessage(e, 'This feature is temporarily unavailable. Please try again later.')
        safeToast(this, { message: this.rewardActionBlockedReason, icon: 'cross' })
      } finally {
        this.claimingUpgrade = false
      }
    },
    async claimSign() {
      if (this.claimingSign) return
      if (this.hasRewardActionBlock()) return
      this.claimingSign = true
      try {
        const res = await SignIn({})
        if (handleNeedLogin(this, res)) return
        if (this.isReceiveSuccess(res)) {
          this.rewardActionBlockedReason = ''
          const content = contentOf(res)
          const amount = this.claimAmount(content, this.signAward)
          this.signStatus = 2
          this.signAward = amount
          safeToast(this, { message: '+' + this.getCurrency + this.$formatNumberWithCommas(amount), icon: 'success' })
        } else {
          this.rewardActionBlockedReason = failMessage(res, 'This feature is temporarily unavailable. Please try again later.')
          safeToast(this, { message: this.rewardActionBlockedReason, icon: 'cross' })
        }
      } catch (e) {
        this.rewardActionBlockedReason = failMessage(e, 'This feature is temporarily unavailable. Please try again later.')
        safeToast(this, { message: this.rewardActionBlockedReason, icon: 'cross' })
      } finally {
        this.claimingSign = false
      }
    },
    async claimBirthday() {
      if (this.claimingBirthday) return
      if (this.hasRewardActionBlock()) return
      this.claimingBirthday = true
      try {
        const res = await ReceiveBirthdayAward({})
        if (handleNeedLogin(this, res)) return
        if (this.isReceiveSuccess(res)) {
          this.rewardActionBlockedReason = ''
          const content = contentOf(res)
          this.birthdayStatus = 2
          const amount = this.claimAmount(content, this.birthdayAward)
          safeToast(this, { message: '+' + this.getCurrency + this.$formatNumberWithCommas(amount), icon: 'success' })
        } else {
          this.rewardActionBlockedReason = failMessage(res, 'This feature is temporarily unavailable. Please try again later.')
          safeToast(this, { message: this.rewardActionBlockedReason, icon: 'cross' })
        }
      } catch (e) {
        this.rewardActionBlockedReason = failMessage(e, 'This feature is temporarily unavailable. Please try again later.')
        safeToast(this, { message: this.rewardActionBlockedReason, icon: 'cross' })
      } finally {
        this.claimingBirthday = false
      }
    },
    async claimCashBack() {
      if (this.claimingCashBack) return
      if (this.hasRewardActionBlock()) return
      this.claimingCashBack = true
      try {
        const res = await GetCashBack({})
        if (handleNeedLogin(this, res)) return
        if (this.isReceiveSuccess(res)) {
          this.rewardActionBlockedReason = ''
          const content = contentOf(res)
          this.cashBackStatus = 2
          const amount = this.claimAmount(content, this.cashBackAmount)
          safeToast(this, { message: '+' + this.getCurrency + this.$formatNumberWithCommas(amount), icon: 'success' })
        } else {
          this.rewardActionBlockedReason = failMessage(res, 'This feature is temporarily unavailable. Please try again later.')
          safeToast(this, { message: this.rewardActionBlockedReason, icon: 'cross' })
        }
      } catch (e) {
        this.rewardActionBlockedReason = failMessage(e, 'This feature is temporarily unavailable. Please try again later.')
        safeToast(this, { message: this.rewardActionBlockedReason, icon: 'cross' })
      } finally {
        this.claimingCashBack = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.vip-page {
  min-height: 100vh;
  background: #1a1d29;
  color: #e3e4f5;
  padding-bottom: 40px;
}

.vip-header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: #1a1d29;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  height: 50px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  justify-content: space-between;

  &__title {
    font-size: var(--vk-fs-md);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #fff;
  }

  &__balance {
    background: #212536;
    border-radius: 20px;
    padding: 4px 4px 4px 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--vk-fs-sm);
    font-weight: 600;
    color: #fff;
    border: 1px solid rgba(100, 116, 139, 0.3);
  }

  &__add {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #ffb020;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.vip-diagnostics {
  padding: 8px 16px 0;
}

.vip-diagnostic {
  margin-bottom: 8px;
  padding: 10px 12px;
  border: 1px solid rgba(245, 158, 11, 0.42);
  border-radius: 8px;
  background: rgba(245, 158, 11, 0.12);
  color: #f8d48a;
  font-size: var(--vk-fs-xs);
  line-height: 18px;

  &--blocked {
    border-color: rgba(239, 68, 68, 0.45);
    background: rgba(239, 68, 68, 0.12);
    color: #fecaca;
  }
}

.vip-emblem {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  gap: 8px;

  &__arrow {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #1b1f2e;
    border-radius: 4px;
  }

  &__badge {
    text-align: center;
    width: 140px;
    height: 140px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  &__img {
    width: 100px;
    height: 100px;
    object-fit: contain;
  }

  &__img--locked {
    filter: grayscale(0.5) brightness(0.75);
  }

  &__locked {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__lock-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.4);
    border-radius: 50%;
  }

  &__name {
    margin-top: 4px;
    font-size: var(--vk-fs-base);
    font-weight: 700;
    color: #ffb020;
    text-transform: uppercase;
  }
  &__tier {
    margin-left: 5px; /* 品牌命名档(继承金色;铁律②VIP=金) */
  }
  &__tier--inferno {
    /* 火焰顶级:火焰橙渐变字 */
    background: var(--vk-grad);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    font-family: var(--vk-font-head);
  }
}

.vip-progress {
  margin: 0 16px 24px;
  background: #111420;
  padding: 20px;
  border-left: 4px solid #ffb020;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 4px;
  }

  &__label {
    font-size: var(--vk-fs-2xs);
    font-weight: 800;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: #a8aaba;
  }

  &__current {
    font-size: var(--vk-fs-xl);
    font-weight: 700;
    color: #ffb020;
  }

  &__sep {
    color: #a8aaba;
    margin: 0 2px;
  }

  &__max {
    font-size: var(--vk-fs-xs);
    color: #a8aaba;
  }

  &__sub {
    font-size: var(--vk-fs-lg);
    font-weight: 700;
    color: #e3e4f5;
    margin-bottom: 12px;
  }

  &__bar {
    width: 100%;
    height: 28px;
    background: #212536;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.5);
    position: relative;
  }

  &__fill {
    height: 100%;
    background: linear-gradient(90deg, #ffb020, #ffb020);
    border-radius: 14px;
    box-shadow: 0 0 15px rgba(255, 176, 32, 0.4);
    transition: width 0.5s ease;

    &--gold {
      background: linear-gradient(90deg, #f8f510, #ffd700);
      box-shadow: 0 0 15px rgba(248, 245, 16, 0.4);
    }
  }

  &__remaining {
    margin-top: 12px;
    text-align: center;
    font-size: var(--vk-fs-xs);
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #a8aaba;
  }
}

.vip-privileges {
  padding: 0 16px;

  &__title {
    font-size: var(--vk-fs-md);
    font-weight: 800;
    color: #e3e4f5;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  &__bar {
    width: 4px;
    height: 20px;
    background: #f8f510;
    display: inline-block;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
}

.vip-card {
  position: relative;
  background: #1b1f2e;
  border-radius: 10px;
  padding: 14px;
  border: 1px solid rgba(69, 71, 85, 0.3);

  &--full {
    grid-column: span 2;
  }

  &--locked .vip-card__content {
    opacity: 0.5;
    filter: grayscale(0.5);
  }

  &__lock {
    position: absolute;
    top: 8px;
    right: 8px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 10px;

    &--row {
      flex-direction: row;
      align-items: center;
    }
  }

  &__icon {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #212536;
    border-radius: 4px;
  }

  &__label {
    font-size: var(--vk-fs-2xs);
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #a8aaba;
    margin-bottom: 2px;
  }

  &__value {
    font-size: var(--vk-fs-15);
    font-weight: 700;
    color: #f8f510;

    &--locked {
      color: #727583;
    }
  }

  &__claim {
    margin-top: 8px;
    width: 100%;
    font-size: var(--vk-fs-2xs);
    font-weight: 800;
    padding: 6px 0;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: rgba(255, 255, 255, 0.05);
    color: #4b5563;

    &--active {
      background: #ff5a1f; /* 激活 Tab=火焰橙(铁律①,VIP 主题用金,动作用橙) */
      color: #fff;
      box-shadow: 0 0 8px rgba(255, 90, 31, 0.4);
    }

    &--done {
      background: rgba(255, 255, 255, 0.05);
      color: #727583;
      cursor: not-allowed;
    }

    &:disabled {
      cursor: not-allowed;
    }

    &--active:active {
      transform: scale(0.95);
    }
  }
}

@media (min-width: 769px) {
  .vip-page {
    max-width: var(--vk-pc-col);
    margin: 0 auto;
  }
}

// ====== VIP GIFTS ======
.vip-gifts {
  padding: 0 16px;
  margin-top: 24px;

  &__title {
    font-size: var(--vk-fs-md);
    font-weight: 800;
    color: #e3e4f5;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  &__bar {
    width: 4px;
    height: 20px;
    background: #ffb020;
    display: inline-block;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
}

.vip-gift-item {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #1b1f2e;
  border-radius: 10px;
  padding: 14px;
  border: 1px solid rgba(69, 71, 85, 0.3);

  &__icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #212536;
    border-radius: 8px;
    flex-shrink: 0;
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: var(--vk-fs-sm);
    font-weight: 600;
    color: #e3e4f5;
  }

  &__amount {
    font-size: var(--vk-fs-15);
    font-weight: 700;
    color: #f8f510;
    margin-top: 2px;
  }

  &__status {
    font-size: var(--vk-fs-xs);
    color: #727583;
    margin-top: 2px;
  }

  &__btn {
    flex-shrink: 0;
    font-size: var(--vk-fs-xs);
    font-weight: 900;
    padding: 8px 16px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &--claim {
      background: #ff5a1f; /* 领取 CTA=火焰橙(铁律①) */
      color: #fff;
      box-shadow: 0 0 10px rgba(255, 90, 31, 0.4);
    }

    &--done {
      background: rgba(255, 255, 255, 0.05);
      color: #727583;
      cursor: not-allowed;
    }

    &--locked {
      background: rgba(255, 255, 255, 0.05);
      color: #4b5563;
      cursor: not-allowed;
    }

    &:disabled {
      cursor: not-allowed;
    }

    &--claim:active {
      transform: scale(0.95);
    }
  }
}
</style>
